import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { Store } from '@ngxs/store';

import { ApolloRoutes } from '@app/client/client.module';
import { params } from '@app/configurations';
import { STORAGE_KEYS } from '@app/core/models/storage-keys.models';
import { TranslateService } from '@ngx-translate/core';
import { Customer } from '../models/customer.models';
import { GetAllAccounts } from '../store/states/accounts/accounts-state.actions';
import { AccountsState } from '../store/states/accounts/accounts.state';
import { GetCustomerCurrencyRate } from '../store/states/currency/currency-state.actions';
import { GetCustomer } from '../store/states/customer/customer-state.actions';
import { CustomerState } from '../store/states/customer/customer.state';
import { GoToAgencyAdmin } from '../store/states/router-nav/router-nav-state.actions';
import { GetUser, GetUserSettings } from '../store/states/user/user-state.actions';
import { IsBilledApp, IsPortalApp, IsSignupApp } from '../utilities/app.utilities';
import { getCookieByKey, SetBrowserAgentClass, SetOSClass } from '../utilities/dom.utilities';
import { GetOAuthPath, sanitizeNullQueryParams, setPortalParams } from '../utilities/url.utilities';
import { LocaleService } from '@app/core/services/locale.service';

export const initializeAppFactory = (store: Store, translate: TranslateService, lcls: LocaleService) => () => {
  if (IsPortalApp()) {
    setPortalParams();
  } else {
    const params = sanitizeNullQueryParams(); // remove null query Params

    if (params) {
      location.search = `?${params.join('&')}`;
      return EMPTY;
    }
  }

  // Setup - DOM utilities
  SetBrowserAgentClass();
  SetOSClass();

  const signUpSync = localStorage.getItem(STORAGE_KEYS.SIGN_UP_SYNC);
  if (signUpSync) {
    localStorage.removeItem(STORAGE_KEYS.SIGN_UP_SYNC);
    localStorage.removeItem(STORAGE_KEYS.CUSTOMER_ID);
    location.replace(`${location.origin}/signup/syncing`);
    return EMPTY;
  }

  // when in signup and there is no xsrf cookie, it means there was no previous login
  if (IsBilledApp() || IsPortalApp() || (IsSignupApp() && !getCookieByKey('xsrf'))) {
    return lcls.initLanguage();
  }

  // Main dispatch flow
  return store
    .dispatch([new GetUser(), ...(IsSignupApp() ? [] : [new GetUserSettings(), new GetCustomerCurrencyRate()])])
    .pipe(
      switchMap(() => lcls.initLanguage()),
      switchMap(() => store.dispatch(new GetCustomer())),
      switchMap(() =>
        store
          .selectOnce(CustomerState.customer)
          .pipe(
            switchMap((customer) =>
              customer && !IsSignupApp() && !IsPortalApp() ? checkAccountsCustomerAccess(store, customer) : of(void 0),
            ),
          ),
      ),
      catchError(() => of(void 0)),
    );
};

function checkAccountsCustomerAccess(store: Store, customer: Customer): Observable<void> {
  return store.dispatch(new GetAllAccounts(customer.customerId)).pipe(
    switchMap(() =>
      combineLatest([store.selectOnce(AccountsState.hasAccounts), store.selectOnce(CustomerState.isAgencyAdmin)]),
    ),
    map(([hasAccounts, isAgencyAdmin]) => {
      if (!hasAccounts && isAgencyAdmin) {
        if (
          ApolloRoutes.some(
            (route) => location.pathname.startsWith(`/${route.path}`) && route.data?.noEmptyAgencyAccess,
          )
        ) {
          store.dispatch(new GoToAgencyAdmin(customer));
        }
      }
    }),
  );
}

export function transformLegacyParamsFactory(): () => Promise<any> {
  return (): Promise<any> =>
    new Promise((resolve) => {
      // for google-mc oauth
      if (location.href.includes('/oauth/google-mc/')) {
        window.history.replaceState(null, null, GetOAuthPath(params.CUSTOMER_ID, 'google-mc'));
      }

      // for amazon-sp oauth
      else if (location.href.includes('/oauth/amazon-sp/')) {
        window.history.replaceState(null, null, GetOAuthPath(params.CUSTOMER_ID, 'amazon-sp'));
      }

      resolve(true);
    });
}
